<template>
  <div>
    <div class="login-container">
      <v-card
        class="login-card elevation-24"
        :style="{ background: getThemeColor }"
        rounded="lg"
      >
        <v-card-text class="pa-8">
          <!-- Header with Logo -->
          <div class="text-center mb-6">
            <div>
              <v-avatar size="80" class="mb-4 elevation-8">
                <v-img
                  v-if="schoolLogo"
                  contain
                  max-height="100%"
                  :src="schoolLogo"
                ></v-img>
                <v-icon size="40" color="white" v-else
                  >mdi-account-circle</v-icon
                >
              </v-avatar>
              <h1 class="display-1 font-weight-light text-white mb-2">
                {{ userCompany?.name || "Welcome Back" }}
              </h1>
            </div>
            <p class="subtitle-1 text-white opacity-80">
              Sign in to your account
            </p>
          </div>

          <!-- Login Form -->
          <v-form
            ref="loginForm"
            v-model="formValid"
            @submit.prevent="handleLogin"
          >
            <!-- Email Field -->
            <v-text-field
              dark
              v-model="userEmail"
              :rules="emailRules"
              label="Email Address"
              placeholder="Enter your email address"
              prepend-inner-icon="mdi-email"
              variant="outlined"
              color="white"
              class="custom-input mb-4"
              :loading="loading"
            ></v-text-field>

            <!-- Password Field -->
            <v-text-field
              dark
              v-model="userPassword"
              :rules="passwordRules"
              label="Password"
              placeholder="Enter your password"
              prepend-inner-icon="mdi-lock"
              :append-icon="showPass ? 'mdi-eye' : 'mdi-eye-off'"
              :type="showPass ? 'text' : 'password'"
              variant="outlined"
              color="white"
              class="custom-input mb-6"
              :loading="loading"
              @click:append="showPass = !showPass"
              @keyup.enter="handleLogin"
            ></v-text-field>

            <!-- Submit Button -->
            <v-btn
              @click="handleLogin"
              :loading="loading"
              :disabled="!formValid || loading"
              block
              size="large"
              class="custom-btn mb-4"
              elevation="8"
            >
              Sign In
              <v-icon right dark class="ml-2">mdi-arrow-right</v-icon>
            </v-btn>
          </v-form>

          <!-- Registration Link -->
          <div class="text-center mt-2">
            <p class="text-white opacity-80 mt-4">
              Don't have an account?
              <v-btn
                @click="$router.push({ path: '/register' })"
                text
                class="text-white pa-0"
                style="text-decoration: underline"
              >
                Register here
              </v-btn>
            </p>
          </div>

          <!-- Reset Password -->
          <div class="text-center">
            <p class="text-white opacity-80">
              Can't remember password?
              <v-btn
                @click="$router.push({ path: '/reset-password' })"
                text
                class="text-white pa-0"
                style="text-decoration: underline"
              >
                Reset Password
              </v-btn>
            </p>
          </div>
        </v-card-text>
      </v-card>

      <!-- Success Animation Overlay -->
      <v-overlay
        v-model="showSuccessOverlay"
        class="d-flex align-center justify-center"
      >
        <div class="text-center">
          <v-icon size="80" color="success" class="mb-4 animate-bounce">
            mdi-check-circle
          </v-icon>
          <h2 class="text-white">Welcome back!</h2>
          <p class="text-white opacity-80">Redirecting you now...</p>
        </div>
      </v-overlay>
    </div>
    <vLoader></vLoader>
  </div>
</template>

<script>
import axios from "@/plugins/axios.js";
import helpersJs from "@/plugins/helpers";
import companiesJs from "@/plugins/companies";
import vLoader from "@/components/Vuetify/Loader.vue";

export default {
  name: "ModernLogin",
  components: {
    vLoader,
  },
  props: {
    slug: {
      type: String,
      default: "",
    },
  },
  watch: {
    slug(newVal) {
      if (newVal) this.determineAppState();
    },
  },
  // async created(){
  //   const data = await this.$store.dispatch('setCompanyContext', {company: null, fresh:true});
  //   this.userCompany = data.targetCompany
  // },
  async mounted() {
    this.$store.commit("setScreenWidth", this.$vuetify.breakpoint.width);
    this.handleQueryParams();
    await this.determineAppState();
  },
  updated() {
    this.$store.commit("setScreenWidth", this.$vuetify.breakpoint.width);
  },
  computed: {
    getApiUrl() {
      return this.$store.getters.getApiUrl;
    },
    getUserCompany() {
      return this.$store.getters.getUserCompany;
    },
    getThemeColor() {
      return this.$store.getters.getThemeColor;
    },
    isMobile() {
      return this.$store.getters.isMobile;
    },
    getCurrentUser() {
      return this.$store.getters.getCurrentUser;
    },
  },

  data() {
    return {
      appName: null,
      userCompany: null,
      schoolLogo: null,
      formValid: false,

      // Form credentials
      userEmail: "",
      userPassword: "",
      showPass: false,

      // UI states
      loading: false,
      showSuccessOverlay: false,

      // Validation rules
      passwordRules: [
        (v) => !!v || "Password is required",
        (v) => (v && v.length >= 6) || "Password must be at least 6 characters",
      ],
      emailRules: [
        (v) => !!v || "Email is required",
        (v) => /.+@.+\..+/.test(v) || "Email must be valid",
      ],
    };
  },

  methods: {
    async determineAppState() {
      const domain = window.location.hostname;

      if (this.slug) {
        const res = await companiesJs.getCompanyBySlug(this.slug);
        if (
          res &&
          res.status === "success" &&
          (await this.setupCompanyData(res.company))
        ) {
          return;
        }
      }

      if (domain.includes("legalcasebilling")) {
        this.appName = "Legal Case Billing";
      } else if (domain.includes("bitsinvoice")) {
        this.appName = "BitsInvoice";
      } else {
        await this.getCompanyWithDomain(domain);
      }
    },
    async setupCompanyData(compData) {
      const company = compData?.company || null;
      if (!company) return false;

      this.userCompany = company;
      this.appName = company?.name || "";
      helpersJs.initialiseOrUpdateCompanyDataInStorage(null, compData);

      if (company.emis) {
        const logos = await companiesJs.getLogos(company.emis);
        if (logos?.length > 0) {
          this.logo = logos[0];
        }
      }
      return true;
    },
    async getCompanyWithDomain(domain) {
      const compData = await companiesJs.getCompanyWithDomain(domain);
      await this.setupCompanyData(compData);
    },
    // Handle Login Submission
    async handleLogin() {
      if (!this.$refs.loginForm.validate()) {
        this.showError("Please enter valid credentials.");
        return;
      }

      this.$store.commit("setLoader", true);
      this.loading = true;

      try {
        const response = await axios.postData("/auth/login", {
          email: this.userEmail,
          password: this.userPassword,
          mode: "password",
        });

        if (response && response.status === "success") {
          await this.handleSuccessfulLogin(response);
        } else {
          this.showError(
            response?.message || "Invalid email or password. Please try again."
          );
        }
      } catch (error) {
        console.error("Login Error:", error);
        this.showError(
          "Login failed. Please check your network and try again."
        );
      } finally {
        this.loading = false;
        this.$store.commit("setLoader", false);
      }
    },

    // Success handling
    async handleSuccessfulLogin(response) {
      this.showSuccessOverlay = true;

      if (response.token && response.user) {
        helpersJs.initialiseOrUpdateCompanyDataInStorage(
          response.user,
          response.company
        );
        this.$store.commit("setAccessToken", response.token);
        this.$store.commit("setAuthenticated", true);
      }

      // Delay for animation
      setTimeout(() => {
        this.$toast.success("Login Successful, Welcome!", { duration: 1000 });
        //this.$emit("login")
        window.location.reload();
      }, 2000);
    },

    // Query parameter handling (NFC / auto-login query hooks)
    handleQueryParams() {
      const query = this.$route.query;

      if (query.mid) {
        this.getMemberOrRegisterMember();
      }
    },

    async getMemberOrRegisterMember() {
      const queryObj = this.$route.query;
      this.loading = true;

      try {
        const response = await axios.postData("/nfc-login", {
          mid: queryObj.mid,
        });

        if (response && response.status === "success") {
          await this.handleSuccessfulLogin(response);
        } else {
          this.showError("Unauthorized Access Denied!");
        }
      } catch (error) {
        this.showError("Access denied!");
      } finally {
        this.loading = false;
      }
    },

    showError(message) {
      this.$toast.error(message, { duration: 5000 });
    },
  },
};
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  overflow-y: auto;
}

.login-card {
  width: 100%;
  max-width: 450px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.custom-input {
  margin-bottom: 16px;
}

.custom-input ::v-deep(.v-field) {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
}

.custom-input ::v-deep(.v-field__input) {
  color: white;
}

.custom-input ::v-deep(.v-label) {
  color: rgba(255, 255, 255, 0.8);
}

.custom-input ::v-deep(.v-field__prepend-inner .v-icon) {
  color: rgba(255, 255, 255, 0.8);
}

.custom-btn {
  background: rgba(255, 255, 255, 0.2) !important;
  color: white !important;
  border-radius: 12px;
  font-weight: 600;
  text-transform: none;
  backdrop-filter: blur(10px);
}

.custom-btn:hover {
  background: rgba(255, 255, 255, 0.3) !important;
}

/* Animations */
@keyframes bounce {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
}

.animate-bounce {
  animation: bounce 1s ease-in-out;
}

/* Responsive design */
@media (max-width: 600px) {
  .login-container {
    padding: 10px;
  }

  .login-card {
    max-width: 100%;
  }
}
</style>
